import { css } from '@emotion/react';
import { faSync } from '@fortawesome/pro-solid-svg-icons';
import { useMemo } from 'react';
import { RadioGroupComponent } from '~/_shared/baseComponents/radio/radioGroup.component';
import { createColor } from '~/_shared/components/colorPicker/colorPicker.helpers';
import type { ColorResult } from '~/_shared/components/colorPicker/colorPicker.types';
import type { HexColor } from '~/_shared/utils/colors/colors.types';
import { useTranslation } from '~/_shared/utils/hooks';
import { RangeType } from '~/_shared/utils/range/range.helpers';
import { BOUNDARY_SETTINGS_MIN_RANGES_COUNT } from '~/boundary/settings/boundarySettings.common';
import {
  BOUNDARY_GROUP_TIE_ID, type BOUNDARY_MAX_VALUE_ID, type BOUNDARY_MIN_VALUE_ID, BOUNDARY_NO_DATA_ID,
  isBoundaryTerritorySpecial, rangeTypeToBucketType,
} from '~/boundary/settings/boundarySettings.helpers';
import {
  boundaryFillDefaultOpacity, getBoundaryTerritoryGroupSettingsStyleDefaults, restoreBTGDefaultColors,
} from '~/boundary/settings/defaultBoundaryTerritoryStyles';
import { FillOpacitySliderComponent } from '~/boundary/settings/fillOpacitySlider/fillOpacitySlider.component';
import { NumberOfRangesComponent } from '~/boundary/settings/numberOfRanges/numberOfRanges.component';
import { getBoundaryMaxRange } from '~/boundary/settings/numeric/boundarySettingsNumeric.helpers';
import { RangeColorsComponent } from '~/boundary/settings/rangeColors/rangeColors.component';
import { BoundarySettingsRestoreButtonComponent } from '~/boundary/settings/restoreButton/boundarySettingsRestoreButton.component';
import { DEFAULT_RANGES_COUNT } from '~/boundary/settings/useBoundaryFillSettingsPropsVariants';
import type {
  BoundaryTerritory, BoundaryTerritoryGroup, BoundaryTerritoryGroupStyle,
} from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { recalculateBoundaryTerritoryColors } from './boundaryFillSettings.helpers';

const boundarySettingsWrapperStyle = css({
  display: 'flex',
  gap: 20,
  padding: '20px 24px',
});

const boundaryColumnStyle = css({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
});

const opacityStyle = css({
  marginTop: 'auto',
});

const rangeTypeRadioGroupStyle = css({
  gap: 25,
  marginTop: 25,
});

const boundarySettingsActionsWrapperStyle = css({
  padding: '5px 24px 20px',
  display: 'flex',
  justifyContent: 'space-between',
});

const restoreColorsButtonStyle = css({
  padding: 0,
});

export const BoundaryFillSettingsNumericalGeneralComponent = ({
  boundaryTerritoryGroup,
  disableRangeTypeChange,
  sharedOpacity,
  isDecimal,
  rangeMax,
  rangeMin,
  rangeType,
  onBoundaryTerritoryGroupChange,
  onNumberOfRangesChange,
  onRestoreDefaultColors,
}: Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  disableRangeTypeChange: boolean;
  sharedOpacity?: Readonly<{
    value: number;
    onChange: (opacity: number) => void;
  }>;
  isDecimal: boolean;
  rangeMax: number | typeof BOUNDARY_MAX_VALUE_ID;
  rangeMin: number | typeof BOUNDARY_MIN_VALUE_ID;
  rangeType: RangeType;
  onBoundaryTerritoryGroupChange: (territoryGroup: BoundaryTerritoryGroup) => void;
  onNumberOfRangesChange: (numberOfRanges: number, style: BoundaryTerritoryGroupStyle, rangeType: RangeType) => void;
  // when omitted, colors are reset to the global defaults instead
  onRestoreDefaultColors?: () => void;
}>) => {
  const [t] = useTranslation();

  const style = boundaryTerritoryGroup.settings.style;
  const numberOfRanges = boundaryTerritoryGroup.settings.rangesCount ?? DEFAULT_RANGES_COUNT;

  const maxNumberOfRanges = useMemo(() => {
    if (isDecimal) {
      return 10;
    }

    const boundaryMaxRange = getBoundaryMaxRange(rangeMin, rangeMax);
    return Math.max(boundaryMaxRange, BOUNDARY_SETTINGS_MIN_RANGES_COUNT);
  }, [isDecimal, rangeMin, rangeMax]);

  const handleNumberOfRangesChange = (numberOfRanges: number) => {
    onNumberOfRangesChange(numberOfRanges, style, rangeType);
  };

  const handleChangeRangeType = (rangeType: RangeType) => {
    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        bucketType: rangeTypeToBucketType(rangeType),
      },
    });
  };

  const updateGroupGradientColors = (gradientColors: BoundaryTerritoryGroupStyle['gradientColors']) => {
    const newBoundaryTerritories = recalculateBoundaryTerritoryColors(
      boundaryTerritoryGroup.settings.boundaryTerritories, gradientColors, !!sharedOpacity
    );

    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: newBoundaryTerritories,
        style: {
          ...boundaryTerritoryGroup.settings.style,
          gradientColors,
        },
      },
    });
  };

  const handleChangeLowValueColor = (color: ColorResult) => {
    updateGroupGradientColors({
      ...boundaryTerritoryGroup.settings.style.gradientColors,
      low: { color: color.hex },
    });
  };

  const handleChangeMediumValueColor = (color: ColorResult) => {
    updateGroupGradientColors({
      ...boundaryTerritoryGroup.settings.style.gradientColors,
      medium: { color: color.hex },
    });
  };

  const handleChangeHighValueColor = (color: ColorResult) => {
    updateGroupGradientColors({
      ...boundaryTerritoryGroup.settings.style.gradientColors,
      high: { color: color.hex },
    });
  };

  const handleChangeMinimalValueColor = (color: ColorResult) => {
    updateGroupGradientColors({
      ...boundaryTerritoryGroup.settings.style.gradientColors,
      minimal: { color: color.hex },
    });
  };

  const handleRestoreDefaultColorsFallback = () => {
    const gradientColors = getBoundaryTerritoryGroupSettingsStyleDefaults().gradientColors;
    let newBoundaryTerritories: ReadonlyArray<BoundaryTerritory> = resetSpecialBoundaryTerritoriesColors(
      boundaryTerritoryGroup.settings.boundaryTerritories);
    newBoundaryTerritories = recalculateBoundaryTerritoryColors(
      newBoundaryTerritories,
      gradientColors
    );

    onBoundaryTerritoryGroupChange({
      ...boundaryTerritoryGroup,
      settings: {
        ...boundaryTerritoryGroup.settings,
        boundaryTerritories: newBoundaryTerritories,
        style: {
          ...restoreBTGDefaultColors(boundaryTerritoryGroup).settings.style,
          gradientColors,
        },
      },
    });
    sharedOpacity?.onChange(boundaryFillDefaultOpacity * 100);
  };

  const resetSpecialBoundaryTerritoriesColors = (boundaryTerritories: ReadonlyArray<BoundaryTerritory>) => boundaryTerritories.map(
    bt => {
      if (!isBoundaryTerritorySpecial(bt)) {
        return bt;
      }

      let style: { color: HexColor; opacity: number };

      switch (bt.boundaryTerritoryId) {
        case BOUNDARY_GROUP_TIE_ID: {
          style = { color: '#000', opacity: 50 };
          break;
        }
        case BOUNDARY_NO_DATA_ID: {
          style = { color: '#ccc', opacity: 50 };
          break;
        }
        default:
          style = { color: '#fff', opacity: 0 };
      }

      return {
        ...bt,
        style: {
          ...bt.style,
          ...style,
        },
      };
    });

  return (
    <>
      <div css={boundarySettingsWrapperStyle}>
        <div css={boundaryColumnStyle}>
          <RangeColorsComponent
            highValueColor={createColor(style.gradientColors.high.color)}
            onHighValueColorChange={handleChangeHighValueColor}
            mediumValueColor={createColor(style.gradientColors.medium.color)}
            onMediumValueColorChange={handleChangeMediumValueColor}
            lowValueColor={createColor(style.gradientColors.low.color)}
            onLowValueColorChange={handleChangeLowValueColor}
            minimalValueColor={createColor(style.gradientColors.minimal.color)}
            onMinimalValueColorChange={handleChangeMinimalValueColor}
          />
        </div>

        <div css={boundaryColumnStyle}>
          <NumberOfRangesComponent
            maxNumberOfRanges={maxNumberOfRanges}
            numberOfRanges={Math.min(numberOfRanges, maxNumberOfRanges)}
            onNumberOfRangesChange={handleNumberOfRangesChange}
          />

          {sharedOpacity && (
            <FillOpacitySliderComponent
              css={opacityStyle}
              label={t('Color Opacity')}
              onChange={sharedOpacity.onChange}
              value={sharedOpacity.value}
            />
          )}

          {!disableRangeTypeChange && (
            <RadioGroupComponent
              css={rangeTypeRadioGroupStyle}
              selectedValue={rangeType}
              onValueChange={handleChangeRangeType}
              items={[{
                value: RangeType.Percentage,
                label: t('Percentage Ranges'),
              }, {
                value: RangeType.Value,
                label: t('Value Ranges'),
              }]}
            />
          )}
        </div>
      </div>

      <div css={boundarySettingsActionsWrapperStyle}>
        <BoundarySettingsRestoreButtonComponent
          css={restoreColorsButtonStyle}
          prefixIcon={faSync}
          text={t('Restore Default Colors')}
          onClick={onRestoreDefaultColors ?? handleRestoreDefaultColorsFallback}
        />
      </div>
    </>
  );
};
